import React from 'react';
import * as echarts from 'echarts';
import './RevenueSummaryCard.less';
import i2Math from '@/utils/i2Math';
import { renderFormattedInt } from '@/utils/renderUtils';

class RevenueSummaryCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedMonth: 11 // 默认选中11月
    };
    
    this.chartRef = React.createRef();
    this.chartInstance = null;
    this.initChartTimer = null; // 延迟初始化定时器
    this.isInitializing = false; // 初始化标志，防止重复初始化
    this.lastInitKey = null; // 上次初始化的 key，用于判断是否需要重新初始化
    this.updateTimer = null; // 防抖定时器
  }

  /** 将接口中的 month（'01' / 10 等）转为 1–12 的月份序号，非法则返回 -1 */
  monthToIndex1To12 = (month) => {
    const n = parseInt(String(month), 10);
    if (!Number.isFinite(n)) return -1;
    if (n < 1 || n > 12) return -1;
    return n;
  };

  /**
   * 无 store_id 时：总额用 all.total_sum；柱状图为全部门店按月汇总（store_map 内 list 的 sum 相加）
   */
  buildAggregatedStoreView = (all) => {
    const { store_map: storeMap, total_sum: totalSum } = all || {};
    if (!storeMap || typeof storeMap !== 'object') {
      return null;
    }
    const monthly = Array.from({ length: 12 }, () => 0);
    Object.values(storeMap).forEach((store) => {
      (store.list || []).forEach((item) => {
        const m = this.monthToIndex1To12(item.month);
        if (m === -1) return;
        monthly[m - 1] = i2Math.i2Add(monthly[m - 1], Number(item.sum) || 0);
      });
    });
    const list = monthly.map((sum, i) => ({ month: i + 1, sum }));
    let total = totalSum;
    if (total == null) {
      total = list.reduce((acc, row) => i2Math.i2Add(acc, Number(row.sum) || 0), 0);
    }
    return { total, list };
  };

  /** 有 store_id：单店；无 store_id：全部门店汇总 */
  getStoreObj = () => {
    const { erpStoreAnnualStatisticsObj, queryManager } = this.props;
    const all = erpStoreAnnualStatisticsObj?.all;
    if (!all) {
      return null;
    }

    const storeId = queryManager?.currentQuery?.store_id;
    const hasStoreId = storeId !== undefined && storeId !== null && String(storeId).trim() !== '';

    if (hasStoreId) {
      const id = String(storeId);
      return all.store_map?.[id] || null;
    }

    return this.buildAggregatedStoreView(all);
  };

  /** 用于 initChart 判断数据是否变化，避免过长 JSON.stringify */
  getChartDataSignature = (storeObj) => {
    if (!storeObj?.list?.length) return '';
    const listSig = storeObj.list.map((row) => row.sum).join(',');
    return `${storeObj.total}_${listSig}`;
  };

  // 计算自适应字体大小
  getResponsiveFontSize = (minSize, preferredVh, maxSize) => {
    const vh = window.innerHeight / 100;
    const preferredSize = preferredVh * vh;
    return Math.max(minSize, Math.min(preferredSize, maxSize));
  };

  // 初始化图表
  initChart = () => {
    if (!this.chartRef.current) return;

    const storeObj = this.getStoreObj();
    if (!storeObj?.list?.length) {
      return;
    }

    // 生成初始化 key（单店 / 全部门店 + 数据签名）
    const { queryManager } = this.props;
    const sid = queryManager?.currentQuery?.store_id;
    const modePrefix =
      sid !== undefined && sid !== null && String(sid).trim() !== '' ? String(sid) : 'ALL';
    const initKey = `${modePrefix}_${this.getChartDataSignature(storeObj)}`;
    
    // 如果数据没有变化且图表已存在，只更新数据，不重新初始化
    if (this.chartInstance && this.lastInitKey === initKey) {
      this.updateChartData(storeObj);
      return;
    }

    // 如果正在初始化，取消之前的初始化
    if (this.isInitializing) {
      if (this.initChartTimer) {
        cancelAnimationFrame(this.initChartTimer);
        this.initChartTimer = null;
      }
      return;
    }

    // 检查容器高度，如果为 0 则延迟初始化
    const containerHeight = this.chartRef.current.offsetHeight;
    if (containerHeight === 0) {
      this.isInitializing = true;
      this.initChartTimer = requestAnimationFrame(() => {
        this.initChartTimer = null;
        this.isInitializing = false;
        if (this.chartRef.current?.offsetHeight > 0) {
          this.initChart();
        }
      });
      return;
    }

    this.isInitializing = true;
    this.lastInitKey = initKey;

    // 如果图表已存在，先销毁
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }

    this.chartInstance = echarts.init(this.chartRef.current);

    // 计算自适应字体大小
    const axisLabelFontSize = this.getResponsiveFontSize(8, 1.2, 12);
    const tooltipFontSize = this.getResponsiveFontSize(10, 1.5, 14);

    const { amounts, labels } = storeObj.list.reduce((acc, item) => {
      acc.amounts.push(item.sum || 0);
      acc.labels.push(`${item.month}月`);
      return acc;
    }, { amounts: [], labels: [] });

    const option = {
      grid: {
        left: '5%',
        right: '5%',
        top: '5%',
        bottom: '1%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: {
          color: '#ffffff',
          fontSize: axisLabelFontSize
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.3)'
          }
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#ffffff',
          fontSize: axisLabelFontSize,
          formatter: (value) => {
            if (value >= 10000) {
              return (value / 10000).toFixed(1) + '万';
            }
            return value;
          }
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.3)'
          }
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: '#ffffff',
        borderWidth: 1,
        textStyle: {
          color: '#ffffff',
          fontSize: tooltipFontSize
        },
        formatter: (params) => {
          const param = params[0];
          return `${param.name}<br/>金额: ${renderFormattedInt(param.value)}元`;
        }
      },
      series: [
        {
          name: '月总金额',
          type: 'bar',
          data: amounts,
          itemStyle: {
            color: '#ffffff',
            borderRadius: [2, 2, 0, 0]
          },
          emphasis: {
            itemStyle: {
              color: '#f0f0f0'
            }
          },
          label: {
            show: false
          }
        }
      ]
    };

    this.chartInstance.setOption(option);

    // 监听点击事件，更新选中月份
    this.chartInstance.on('click', (params) => {
      // name 格式可能是 "2025年10月" 或 "10月"，提取月份数字
      const monthMatch = params.name.match(/(\d+)月/);
      if (monthMatch) {
        const month = parseInt(monthMatch[1]);
        this.setState({ selectedMonth: month });
      }
    });

    this.isInitializing = false;
  };

  // 更新图表数据（不重新初始化）
  updateChartData = (storeObj) => {
    if (!this.chartInstance || !storeObj?.list?.length) {
      return;
    }

    // 提取图表数据
    const { amounts, labels } = storeObj.list.reduce((acc, item) => {
      acc.amounts.push(item.sum || 0);
      acc.labels.push(`${item.month}月`);
      return acc;
    }, { amounts: [], labels: [] });

    // 只更新数据，不重新初始化
    this.chartInstance.setOption({
      xAxis: {
        data: labels
      },
      series: [{
        data: amounts
      }]
    }, { notMerge: false });
  };

  componentDidMount() {
    this.resizeTimer = null;
    this.initChart();
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps) {
    const { erpStoreAnnualStatisticsObj, queryManager } = this.props;

    const prevSid = prevProps.queryManager?.currentQuery?.store_id;
    const curSid = queryManager?.currentQuery?.store_id;
    const prevHasStore =
      prevSid !== undefined && prevSid !== null && String(prevSid).trim() !== '';
    const curHasStore =
      curSid !== undefined && curSid !== null && String(curSid).trim() !== '';

    const storeIdChanged =
      prevHasStore !== curHasStore ||
      (prevHasStore && curHasStore && String(prevSid) !== String(curSid));

    const prevAll = prevProps.erpStoreAnnualStatisticsObj?.all;
    const curAll = erpStoreAnnualStatisticsObj?.all;

    let storeDataChanged = false;
    if (!storeIdChanged) {
      if (curHasStore) {
        const prevStoreObj = prevAll?.store_map?.[String(prevSid)];
        const currentStoreObj = curAll?.store_map?.[String(curSid)];
        storeDataChanged = JSON.stringify(prevStoreObj) !== JSON.stringify(currentStoreObj);
      } else {
        storeDataChanged =
          prevAll?.total_sum !== curAll?.total_sum ||
          JSON.stringify(prevAll?.store_map) !== JSON.stringify(curAll?.store_map);
      }
    }

    // 只有当数据真正变化时才重新初始化
    if (storeIdChanged || storeDataChanged) {
      // 如果正在初始化，先等待完成
      if (this.isInitializing) {
        return;
      }
      
      // 清除之前的初始化标志和定时器
      if (this.initChartTimer) {
        cancelAnimationFrame(this.initChartTimer);
        this.initChartTimer = null;
      }
      this.lastInitKey = null; // 清除 key，强制重新初始化
      
      // 使用 setTimeout 防抖，避免快速连续更新
      if (this.updateTimer) {
        clearTimeout(this.updateTimer);
      }
      this.updateTimer = setTimeout(() => {
        this.updateTimer = null;
        this.initChart();
      }, 100); // 100ms 防抖
    }
  }

  componentWillUnmount() {
    // 清理定时器
    if (this.initChartTimer) {
      cancelAnimationFrame(this.initChartTimer);
      this.initChartTimer = null;
    }
    
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }
    
    // 清理防抖定时器
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    
    // 销毁图表
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
    
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    if (this.chartInstance) {
      // 先调整图表大小
      this.chartInstance.resize();
      
      // 防抖：延迟重新初始化以更新字体大小
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        if (this.chartInstance) {
          // 重新初始化图表以更新字体大小
          this.initChart();
        }
      }, 300); // 300ms 防抖延迟
    }
  };

  render() {
    const { dbSummaryObj, queryManager } = this.props;
    
    if (!dbSummaryObj?.data || !queryManager?.currentQuery) {
      return null;
    }

    const { data: { totalCustomerCount } } = dbSummaryObj;
    const storeObj = this.getStoreObj() || {};
    return (
      <div className="revenue-summary-card">
        <div className="revenue-content">
          <div className="revenue-header">
            <div className="revenue-label">门店总金额</div>
            <div className="revenue-amount">{renderFormattedInt(storeObj.total)}</div>
            <div className="revenue-secondary">
              <span className="secondary-amount">{renderFormattedInt(totalCustomerCount)}</span>
              <span className="secondary-label">累计服务客户</span>
            </div>
          </div>

          <div className="revenue-chart-container">
            <div ref={this.chartRef} className="revenue-chart"></div>
          </div>
        </div>
      </div>
    );
  }
}

export default RevenueSummaryCard;
